import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  signal,
} from "@angular/core";
import * as Highcharts from "highcharts";
import {
  HighchartsChartComponent,
  providePartialHighcharts,
} from "highcharts-angular";
import { HBarRow } from "../../../charts/hbar-list/hbar-list.component";

@Component({
  selector: "app-exp-marginal-risk-chart",
  standalone: true,
  imports: [HighchartsChartComponent],
  encapsulation: ViewEncapsulation.None,
  providers: [
    providePartialHighcharts({
      modules: () => [],
    }),
  ],
  template: `
    <div class="exp-mrisk-chart" [style.minHeight.px]="resolvedHeight">
      @if (loaded() && hasData()) {
        <highcharts-chart
          [options]="chartOptions"
          [constructorType]="'chart'"
          style="width:100%; height:100%; display:block;"
        ></highcharts-chart>
      }
      @if (loaded() && !hasData()) {
        <div class="exp-mrisk-empty">No marginal risk data available.</div>
      }
    </div>
  `,
  styles: [
    `
      .exp-mrisk-chart {
        width: 100%;
        position: relative;
      }
      .exp-mrisk-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
        min-height: 200px;
        color: var(--text-faint);
        font-size: 12px;
      }
      .exp-mrisk-chart .highcharts-background {
        fill: transparent !important;
      }
      .exp-mrisk-chart .highcharts-plot-background {
        fill: transparent !important;
      }
      .exp-mrisk-chart .highcharts-grid .highcharts-grid-line {
        stroke: var(--border);
        stroke-dasharray: 2 3;
        opacity: 0.7;
      }
      .exp-mrisk-chart .highcharts-axis-line {
        stroke: transparent !important;
      }
      .exp-mrisk-chart .highcharts-tick {
        stroke: transparent !important;
      }
      .exp-mrisk-chart .highcharts-axis-labels text,
      .exp-mrisk-chart .highcharts-yaxis-labels text,
      .exp-mrisk-chart .highcharts-xaxis-labels text {
        fill: var(--text-dim) !important;
        font-family: var(--font-ui) !important;
        font-size: 11px !important;
        font-weight: 500 !important;
      }
      .exp-mrisk-chart .highcharts-yaxis-labels text {
        fill: var(--text) !important;
      }
      .exp-mrisk-chart .highcharts-data-label text,
      .exp-mrisk-chart .highcharts-data-label tspan {
        fill: var(--text) !important;
        font-family: var(--font-mono) !important;
        font-size: 10.5px !important;
        font-weight: 600 !important;
        text-shadow: none !important;
        stroke-width: 0 !important;
      }
      .exp-mrisk-chart .highcharts-tooltip-box {
        fill: var(--surface-2) !important;
        stroke: var(--border-strong) !important;
        stroke-width: 1 !important;
        fill-opacity: 1 !important;
      }
      .exp-mrisk-chart .highcharts-tooltip text {
        fill: var(--text) !important;
      }
      .exp-mrisk-chart .highcharts-credits {
        display: none;
      }
      .exp-mrisk-chart .highcharts-axis-title {
        fill: var(--text-dim) !important;
      }
    `,
  ],
})
export class ExpMarginalRiskChartComponent implements OnInit, OnChanges {
  @Input() rows: HBarRow[] = [];
  @Input() height: number = 320;
  @Input() format: (v: number) => string = (v) => (v * 100).toFixed(2) + "%";
  @Input() valueIsPct = true;
  @Input() axisTitle = "Risk";

  loaded = signal(false);
  hasData = signal(false);
  chartOptions: Highcharts.Options = {};

  private readonly posColor = "#7C5CFF";
  private readonly negColor = "#FF5C7A";

  get resolvedHeight(): number {
    return Math.max(this.height, 32 * Math.max(this.rows?.length || 0, 1) + 80);
  }


  ngOnInit(): void {
    this.initPlot();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.rows ||
      changes.format ||
      changes.valueIsPct ||
      changes.axisTitle
    ) {
      this.loaded.set(false);
      this.initPlot();
    }
  }

  private initPlot(): void {
    if (!Array.isArray(this.rows) || !this.rows.length) {
      this.hasData.set(false);
      this.loaded.set(true);
      return;
    }

    const sorted = [...this.rows]
      .filter((r) => r && Number.isFinite(r.value))
      .sort((a, b) => b.value - a.value);

    if (!sorted.length) {
      this.hasData.set(false);
      this.loaded.set(true);
      return;
    }

    const scale = this.valueIsPct ? 100 : 1;
    const categories = sorted.map((r) => r.label);
    const data = sorted.map((r) => +(r.value * scale).toFixed(4));

    this.hasData.set(true);
    this.initChart(categories, data);
  }

  private initChart(categories: string[], data: number[]): void {
    const formatFn = this.format;
    const valueIsPct = this.valueIsPct;
    const axisFormat = valueIsPct ? "{value:.0f}%" : "{value:.2f}";
    const totalHeight = this.resolvedHeight;

    this.chartOptions = {
      chart: {
        type: "bar",
        height: totalHeight,
        backgroundColor: "transparent",
        spacing: [10, 8, 8, 4],
        style: { fontFamily: "Inter, system-ui, sans-serif" },
      },
      credits: { enabled: false },
      title: { text: "" },
      xAxis: {
        categories,
        lineColor: "transparent",
        tickColor: "transparent",
        labels: {
          style: {
            color: "var(--text)",
            fontFamily: "var(--font-ui)",
            fontSize: "12px",
            fontWeight: "500",
          },
        },
      },
      yAxis: {
        title: {
          text: this.axisTitle,
          style: { color: "var(--text-dim)", fontSize: "11px" },
        },
        gridLineColor: "var(--border)",
        gridLineDashStyle: "ShortDash",
        gridLineWidth: 1,
        minorGridLineWidth: 0,
        allowDecimals: !valueIsPct,
        labels: {
          format: axisFormat,
          style: { color: "var(--text-dim)" },
        },
        plotLines: [
          {
            value: 0,
            width: 1,
            color: "var(--border-strong)",
            zIndex: 4,
          },
        ],
      },
      legend: { enabled: false },
      tooltip: {
        borderRadius: 8,
        borderWidth: 1,
        padding: 10,
        shadow: false,
        useHTML: true,
        formatter(this: any) {
          const raw = valueIsPct ? this.y / 100 : this.y;
          const isNeg = this.y < 0;
          const color = isNeg ? "#FF5C7A" : "#7C5CFF";
          return `<div style="min-width:200px;font-size:12px;color:var(--text);">
            <div style="font-size:10.5px;letter-spacing:0.06em;color:var(--text-dim);text-transform:uppercase;margin-bottom:4px;font-weight:500;">${this.x}</div>
            <div style="display:flex;align-items:center;gap:10px;margin-top:4px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color};flex:none;"></span>
              <span style="color:var(--text-dim);">Risk</span>
              <span style="margin-left:auto;font-family:var(--font-mono);font-variant-numeric:tabular-nums;font-weight:600;color:var(--text);">${formatFn(raw)}</span>
            </div>
          </div>`;
        },
      },
      plotOptions: {
        bar: {
          colorByPoint: false,
          color: this.posColor,
          negativeColor: this.negColor,
          borderRadius: 3,
          borderWidth: 0,
          pointPadding: 0.08,
          groupPadding: 0.12,
          dataLabels: {
            enabled: true,
            inside: false,
            crop: false,
            overflow: "allow",
            formatter(this: any) {
              const raw = valueIsPct ? this.y / 100 : this.y;
              return formatFn(raw);
            },
            style: {
              color: "var(--text)",
              fontWeight: "600",
              fontSize: "10.5px",
              textOutline: "none",
            },
          },
          states: {
            hover: { brightness: 0.08 },
          },
        },
        series: {
          animation: { duration: 350 },
        },
      },
      series: [
        {
          type: "bar",
          name: "Risk",
          data,
        },
      ],
    };

    setTimeout(() => {
      this.loaded.set(true);
    });
  }
}
